import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { 
  TrophyIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckCircleIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const SalesTargetsPage = () => {
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [filterAgent, setFilterAgent] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  // Fetch agents
  const { data: agentsData } = useQuery({
    queryKey: ['field-agents-list'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/admin/field-agents', {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 1000 }
      });
      return data.agents;
    }
  });

  // Fetch targets
  const { data: targetsData, isLoading } = useQuery({
    queryKey: ['sales-targets', filterAgent, filterStatus],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const { data } = await axios.get('/api/admin/sales-targets', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          agent_id: filterAgent || undefined,
          status: filterStatus || undefined
        }
      });
      return data;
    }
  });

  // Delete target mutation
  const deleteTarget = useMutation({
    mutationFn: async (targetId) => {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/admin/sales-targets/${targetId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['sales-targets']);
      toast.success('היעד נמחק בהצלחה');
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'שגיאה במחיקת יעד');
    }
  });

  const stats = targetsData?.stats || {
    total_targets: 0,
    active_targets: 0,
    completed_targets: 0,
    total_revenue_target: 0,
    total_orders_target: 0,
    total_customers_target: 0
  };

  const getTargetTypeLabel = (type) => {
    const labels = {
      revenue: 'הכנסות',
      orders: 'הזמנות',
      new_customers: 'לקוחות חדשים'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { bg: 'bg-green-100', text: 'text-green-800', label: 'פעיל' },
      completed: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'הושלם' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', label: 'בוטל' }
    };
    const badge = badges[status] || badges.active;
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const calculateProgress = (target) => {
    if (!target.target_value) return 0;
    const achievedValue = parseFloat(target.achieved_value) || 0;
    const targetValue = parseFloat(target.target_value) || 1;
    const progress = (achievedValue / targetValue) * 100;
    return Math.min(progress, 100).toFixed(1);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">יעדי מכירה</h1>
          <p className="text-gray-600 mt-1">ניהול ומעקב אחר יעדי מכירה לסוכנים</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          יעד חדש
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <TrophyIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div className="mr-4">
              <p className="text-sm text-gray-600">סך יעדים</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_targets}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="mr-4">
              <p className="text-sm text-gray-600">יעדים פעילים</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active_targets}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-lg">
              <ChartBarIcon className="w-6 h-6 text-purple-600" />
            </div>
            <div className="mr-4">
              <p className="text-sm text-gray-600">יעדים שהושלמו</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completed_targets}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 rounded-lg">
              <TrophyIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div className="mr-4">
              <p className="text-sm text-gray-600">יעדי הכנסות</p>
              <p className="text-2xl font-bold text-gray-900">
                ₪{stats.total_revenue_target?.toLocaleString()}
              </p>
              <div className="text-xs text-gray-500 mt-1">
                {stats.total_orders_target > 0 && `${stats.total_orders_target} הזמנות | `}
                {stats.total_customers_target > 0 && `${stats.total_customers_target} לקוחות חדשים`}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">סינון לפי סוכן</label>
            <select
              value={filterAgent}
              onChange={(e) => setFilterAgent(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">כל הסוכנים</option>
              {agentsData?.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.user?.first_name} {agent.user?.last_name} ({agent.employee_id})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">סטטוס</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">כל הסטטוסים</option>
              <option value="active">פעיל</option>
              <option value="completed">הושלם</option>
              <option value="cancelled">בוטל</option>
            </select>
          </div>
        </div>
      </div>

      {/* Targets List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">סוכן</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">סוג יעד</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">יעד</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">הושג</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">התקדמות</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">תקופה</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">סטטוס</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">פעולות</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">טוען...</td>
              </tr>
            ) : !targetsData?.targets?.length ? (
              <tr>
                <td colSpan="8" className="px-6 py-4 text-center text-gray-500">לא נמצאו יעדים</td>
              </tr>
            ) : (
              targetsData.targets.map((target) => (
                <tr key={target.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {target.agent?.user?.first_name} {target.agent?.user?.last_name}
                    </div>
                    <div className="text-sm text-gray-500">{target.agent?.employee_id}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{getTargetTypeLabel(target.target_type)}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm font-medium text-gray-900">
                      {target.target_type === 'revenue' 
                        ? `₪${parseFloat(target.target_value || 0).toLocaleString()}` 
                        : target.target_value}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">
                      {target.target_type === 'revenue' 
                        ? `₪${parseFloat(target.achieved_value || 0).toLocaleString()}` 
                        : (target.achieved_value || 0)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="w-full">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">{calculateProgress(target)}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-indigo-600 h-2 rounded-full transition-all"
                          style={{ width: `${calculateProgress(target)}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    <div>{new Date(target.period_start).toLocaleDateString('he-IL')}</div>
                    <div>{new Date(target.period_end).toLocaleDateString('he-IL')}</div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(target.status)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingTarget(target)}
                        className="text-indigo-600 hover:text-indigo-800"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => {
                          if (window.confirm('האם אתה בטוח שברצונך למחוק יעד זה?')) {
                            deleteTarget.mutate(target.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingTarget) && (
        <TargetModal
          target={editingTarget}
          agents={agentsData}
          onClose={() => {
            setShowCreateModal(false);
            setEditingTarget(null);
          }}
          onSuccess={() => {
            queryClient.invalidateQueries(['sales-targets']);
            setShowCreateModal(false);
            setEditingTarget(null);
          }}
        />
      )}
    </div>
  );
};

// Target Modal Component
const TargetModal = ({ target, agents, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    agent_id: target?.agent_id || '',
    target_type: target?.target_type || 'revenue',
    target_value: target?.target_value || '',
    period_start: target?.period_start?.split('T')[0] || '',
    period_end: target?.period_end?.split('T')[0] || '',
    notes: target?.notes || ''
  });

  const mutation = useMutation({
    mutationFn: async (data) => {
      const token = localStorage.getItem('token');
      if (target) {
        await axios.put(`/api/admin/sales-targets/${target.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/admin/sales-targets', data, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    },
    onSuccess: () => {
      toast.success(target ? 'היעד עודכן בהצלחה' : 'היעד נוצר בהצלחה');
      onSuccess();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'שגיאה בשמירת יעד');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full">
        <h2 className="text-2xl font-bold mb-4">{target ? 'עריכת יעד' : 'יעד חדש'}</h2>
        
        <form onSubmit={handleSubmit}>
          {/* Agent Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">סוכן</label>
            <select
              value={formData.agent_id}
              onChange={(e) => setFormData({ ...formData, agent_id: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
              required
              disabled={!!target}
            >
              <option value="">בחר סוכן...</option>
              {agents?.map(agent => (
                <option key={agent.id} value={agent.id}>
                  {agent.user?.first_name} {agent.user?.last_name} ({agent.employee_id})
                </option>
              ))}
            </select>
          </div>

          {/* Target Type */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">סוג יעד</label>
            <select
              value={formData.target_type}
              onChange={(e) => setFormData({ ...formData, target_type: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
              required
            >
              <option value="revenue">הכנסות (₪)</option>
              <option value="orders">מספר הזמנות</option>
              <option value="new_customers">לקוחות חדשים</option>
            </select>
          </div>

          {/* Target Value */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">ערך יעד</label>
            <input
              type="number"
              value={formData.target_value}
              onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
              required
              min="0"
              step="0.01"
            />
          </div>

          {/* Period */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">תחילת תקופה</label>
              <input
                type="date"
                value={formData.period_start}
                onChange={(e) => setFormData({ ...formData, period_start: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">סוף תקופה</label>
              <input
                type="date"
                value={formData.period_end}
                onChange={(e) => setFormData({ ...formData, period_end: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-4 py-2"
                required
              />
            </div>
          </div>

          {/* Notes */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">הערות</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-4 py-2"
              rows="3"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={mutation.isLoading}
              className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
            >
              {mutation.isLoading ? 'שומר...' : 'שמור'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              ביטול
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesTargetsPage;

